import { useQuery } from "@apollo/client/react";
import { type TFunction } from "i18next";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";

import { documentsPath, showDocumentPath } from "@path/document_paths";

import Loading from "@/containers/Loading";
import { DocumentDocument } from "@/gql/graphql";
import { useAppSelector } from "@/hooks";
import useCustomerChange from "@/hooks/useCustomerChange";
import useI18nBreadcrumb from "@/hooks/useI18nBreadcrumb";
import useTitle from "@/hooks/useTitle";
import { sessionSelector } from "@/store/sessionReducer";

import ObjectNotFound from "../ObjectNotFound";
import DocumentDetails from "./DocumentDetails";

const BREADCRUMB_IDENTIFIER = "documents/show";

const breadcrumbItem = (t: TFunction, session: LightSessionInterface, document?: TTDocumentType) => ({
  type: BREADCRUMB_IDENTIFIER,
  to: document ? showDocumentPath(session, document) : "",
  text: t("documents:show.title", { id: document?.id || "…", subject: document?.subject || "…" }),
  after: "documents",
});

type PropsType = {
  id?: string;
  cancelAction?: () => void;
  session?: SessionInterface;
};

export default function Show({ id: propsId, session: propsSession, cancelAction: cancelActionProp }: PropsType) {
  const session = useAppSelector(sessionSelector);
  const { t } = useTranslation(["documents"]);
  const currentSession = propsSession || session;
  const { currentCustomer, currentProject } = currentSession;
  const { id: paramsId } = useParams<"id">();
  const id = propsId || paramsId;
  const navigate = useNavigate();

  const defaultCancelAction = () => {
    navigate(documentsPath(session));
  };
  const cancelAction = cancelActionProp || defaultCancelAction;

  const variables = { customerId: currentCustomer.id, projectId: currentProject.id, id };
  const { data } = useQuery(DocumentDocument, { variables });

  const document = data?.document;

  useTitle(t("documents:show.title", { id: document?.id || "…", subject: document?.subject || "…" }));
  useI18nBreadcrumb(BREADCRUMB_IDENTIFIER, breadcrumbItem, document || undefined, ["documents"]);
  useCustomerChange(documentsPath(session));

  if (document === null) return <ObjectNotFound message={t("documents:document_not_found")} />;
  if (!document) return <Loading stretch />;

  return <DocumentDetails session={currentSession} document={document} cancelAction={cancelAction} />;
}
